import { ClientData, SexEnum } from '../../../models';
import { ColumnMapping } from './autoMap';

// Portugal +351 phone normalisation
export function normalizePhoneNumber(rawPhoneNumber: string): string | null {
    const digits = rawPhoneNumber.replace(/\D/g, '');
    if (!digits) return null;
    if (/^0+$/.test(digits)) return null; // placeholder like 000000000
    if (digits.startsWith('00351') && digits.length === 14) return `+${digits.slice(2)}`;
    if (digits.startsWith('351') && digits.length === 12) return `+${digits}`;
    if (digits.length === 10 && digits.startsWith('0')) return `+351${digits.slice(1)}`;
    if (digits.length === 9) return `+351${digits}`;
    return null;
}

const PHONE_REGEX = /(?<!\d)(?:\+|00)?[\d\s\-()]{8,}\d(?!\d)/g;
const VALID_PORTUGAL_PHONE_REGEX = /^\+351\d{9}$/;
const VALID_GENERIC_PHONE_REGEX = /^\+[1-9]\d{7,16}$/;

export function isValidPhoneNumber(phoneNumber: string): boolean {
    const cleanedNumber = phoneNumber.replace(/[\s\-()]/g, '');
    if (!cleanedNumber) return false;
    if (cleanedNumber.startsWith('+351')) {
        return VALID_PORTUGAL_PHONE_REGEX.test(cleanedNumber);
    }
    return VALID_GENERIC_PHONE_REGEX.test(cleanedNumber);
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Returns the email only if it is structurally valid; placeholder text such as
// "NAO TEM" (Portuguese for "doesn't have") is dropped so the row still imports.
export function normalizeEmail(rawEmail: string): string | undefined {
    const value = rawEmail.trim();
    return value && EMAIL_REGEX.test(value) ? value : undefined;
}

export function partitionPhoneNumbers(phoneNumbers: string[]): { validPhoneNumbers: string[]; invalidPhoneNumbers: string[] } {
    return phoneNumbers.reduce<{ validPhoneNumbers: string[]; invalidPhoneNumbers: string[] }>((acc, phoneNumber) => {
        if (isValidPhoneNumber(phoneNumber)) {
            acc.validPhoneNumbers.push(phoneNumber);
        } else {
            acc.invalidPhoneNumbers.push(phoneNumber);
        }
        return acc;
    }, { validPhoneNumbers: [], invalidPhoneNumbers: [] });
}

function extractPhones(raw: string): string[] {
    const seen = new Set<string>();
    const result: string[] = [];
    for (const match of raw.matchAll(PHONE_REGEX)) {
        const digits = match[0].replace(/\D/g, '');
        const normalized = normalizePhoneNumber(digits) ?? match[0].trim();
        if (normalized && !seen.has(normalized)) {
            seen.add(normalized);
            result.push(normalized);
        }
    }
    return result;
}

const SKIP_DATE_PREFIXES = ['0000-00-00', '1899-12-30'];

export function parseDate(raw: string): string | null {
    const v = raw.trim();
    if (!v) return null;
    if (SKIP_DATE_PREFIXES.some(s => v.startsWith(s))) return null;

    // yyyy-MM-dd (with optional time suffix)
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
        const iso = v.slice(0, 10);
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return iso;
    }

    // dd/MM/yyyy or d/M/yyyy
    const dmy = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (dmy) {
        const [, d, m, y] = dmy;
        const iso = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        const date = new Date(iso);
        if (!isNaN(date.getTime())) return iso;
    }

    return null;
}

export function parseSplitBirthday(rawYear: string, rawMonth: string, rawDay: string): string | null {
    const y = parseInt(rawYear.trim(), 10);
    const m = parseInt(rawMonth.trim(), 10);
    const d = parseInt(rawDay.trim(), 10);
    if (isNaN(y) || isNaN(m) || isNaN(d)) return null;
    if (y < 1000 || y > 9999 || m < 1 || m > 12 || d < 1 || d > 31) return null;
    const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const date = new Date(iso);
    if (isNaN(date.getTime())) return null;
    return iso;
}

const SEX_MAP: Record<string, SexEnum> = {
    m: SexEnum.Male,
    male: SexEnum.Male,
    masculino: SexEnum.Male,
    f: SexEnum.Female,
    female: SexEnum.Female,
    feminino: SexEnum.Female,
};

const TRUTHY = new Set(['1', 'true', 'yes', 'sim', 'oui']);

export function parseRow(
    row: string[],
    headers: string[],
    mapping: ColumnMapping,
): ClientData {
    const firstValue = (fieldId: string): string =>
        (mapping.fieldMap[fieldId] ?? []).map(i => row[i] ?? '').find(v => v.trim()) ?? '';

    const allValues = (fieldId: string): string[] =>
        (mapping.fieldMap[fieldId] ?? []).map(i => row[i] ?? '').filter(v => v.trim());

    // Notes: each assigned column becomes "<header>: <value>"
    const buildNotes = (fieldId: string) =>
        (mapping.fieldMap[fieldId] ?? [])
            .map(i => ({ h: headers[i] ?? '', v: row[i] ?? '' }))
            .filter(({ v }) => v.trim())
            .map(({ h, v }) => `${h}: ${v}`)
            .join('\n') || undefined;

    const phoneNumbers = extractPhones(allValues('phoneNumbers').join(' | '));

    const tags = allValues('tags')
        .flatMap(v => v.split(','))
        .map(t => t.trim())
        .filter((t, i, arr) => t && arr.indexOf(t) === i);

    const properties: Record<string, string> = {};
    for (const [fieldId, indices] of Object.entries(mapping.fieldMap)) {
        if (!fieldId.startsWith('property:')) continue;
        const value = indices.map(i => row[i] ?? '').find(v => v.trim());
        if (value) properties[fieldId.slice('property:'.length)] = value.trim();
    }

    const data: ClientData = { name: firstValue('name').trim() };

    const str = (fieldId: string): string | undefined => firstValue(fieldId).trim() || undefined;

    data.taxNumber = str('taxNumber');
    data.healthNumber = str('healthNumber');
    data.identificationNumber = str('identificationNumber');
    data.subsystemName = str('subsystemName');
    data.subsystemNumber = str('subsystemNumber');
    data.email = normalizeEmail(firstValue('email'));
    data.job = str('job');
    data.origin = str('origin');
    data.addressLine1 = str('addressLine1');
    data.addressLine2 = str('addressLine2');
    data.zipCode = str('zipCode');
    data.city = str('city');
    data.warningNote = str('warningNote');
    data.notes = buildNotes('notes');
    data.internalNotes = buildNotes('internalNotes');

    const birthday = parseDate(firstValue('birthday')) ?? parseSplitBirthday(
        firstValue('birthdayYear'),
        firstValue('birthdayMonth'),
        firstValue('birthdayDay'),
    );
    if (birthday) data.birthday = birthday;

    if (phoneNumbers.length) data.phoneNumbers = phoneNumbers;
    if (tags.length) data.tags = tags;
    if (Object.keys(properties).length) data.properties = properties;

    const sexRaw = firstValue('sex').trim().toLowerCase();
    if (sexRaw) data.sex = SEX_MAP[sexRaw] ?? SexEnum.Unspecified;

    const fileNum = parseInt(firstValue('clientFileNumber'), 10);
    if (!isNaN(fileNum)) data.clientFileNumber = fileNum;

    const rgpdRaw = firstValue('rgpdApproved').trim().toLowerCase();
    if (rgpdRaw) data.rgpdApproved = TRUTHY.has(rgpdRaw);

    const mktRaw = firstValue('acceptMarktingCommunication').trim().toLowerCase();
    if (mktRaw) data.acceptMarktingCommunication = TRUTHY.has(mktRaw);

    return data;
}
